import { mediaDevices } from '@platform';
import config from '../config';
import ProxyService from '../Proxy';
import Utils from '../Utils';
import WebRTCSession from './WebRTCSession';
import WebRTCSignalingProcessor from './WebRTCSignalingProcessor';
import WebRTCSignalingProvider from './WebRTCSignalingProvider';
import WebRTCHelpers from './WebRTCHelpers';
import type { Calls, EmptyResponse } from '../types';
import type { IChatRealTimeService } from '../types/chat';
import {
  CallEvent,
  CallSessionConnectionState,
  CallSessionState,
  CallType,
  PeerConnectionState,
  ProxyMethod,
} from '../types/enums';

export default class WebRTCClient {
  public chatService: IChatRealTimeService;
  public proxy: ProxyService;
  public signalingProcessor: WebRTCSignalingProcessor;
  public signalingProvider: WebRTCSignalingProvider;
  public SessionConnectionState: typeof CallSessionConnectionState = CallSessionConnectionState;
  public PeerConnectionState: typeof PeerConnectionState = PeerConnectionState;
  public CallType: typeof CallType = CallType;
  public sessions: { [key: string]: WebRTCSession } = {};
  declare onCallListener: Calls.OnCallListener;
  declare onAcceptCallListener: Calls.OnAcceptCallListener;
  declare onRejectCallListener: Calls.OnRejectCallListener;
  declare onStopCallListener: Calls.OnStopCallListener;
  declare onInvalidEventsListener: Calls.OnInvalidEventsListener;
  declare onUserNotAnswerListener: Calls.OnUserNotAnswerListener;
  declare onRemoteStreamListener: Calls.OnRemoteStreamListener;
  declare onSessionConnectionStateChangedListener: Calls.OnSessionConnectionStateChangedListener;
  declare onSessionCloseListener: Calls.OnSessionCloseListener;
  declare onCallStatsReport: Calls.OnCallStatsReportListener;
  public onDevicesChangeListener: Calls.OnDevicesChangeListener = () => {};

  constructor(chatService: IChatRealTimeService, proxy: ProxyService) {
    this.chatService = chatService;
    this.proxy = proxy;

    this.signalingProcessor = new WebRTCSignalingProcessor(this);
    this.signalingProvider = new WebRTCSignalingProvider(this.chatService);

    if (mediaDevices) {
      mediaDevices.ondevicechange = Utils.safeCallbackCall(this.onDevicesChangeListener);
    }
  }

  private get currentUserID(): number {
    return this.chatService.currentUserId;
  }

  public async getMediaDevices(kind?: MediaDeviceKind): Promise<MediaDeviceInfo[]> {
    if (mediaDevices?.enumerateDevices) {
      const devices = await mediaDevices.enumerateDevices();
      return kind ? devices.filter((device) => device.kind === kind) : devices;
    } else {
      throw new Error("No 'enumerateDevices' API supported");
    }
  }

  public createNewSession(
    opponentsIDs: number[],
    callType: number,
    options?: { maxBandwidth?: number; bandwidth?: number }
  ): WebRTCSession {
    const maxBandwidth = (options?.maxBandwidth || options?.bandwidth) ?? 0;

    if (!opponentsIDs) {
      throw new Error("Can't create a session without opponentsIDs");
    }

    return this.createAndStoreSession(null, this.currentUserID, opponentsIDs, callType, maxBandwidth);
  }

  private createAndStoreSession(
    sessionID: string | null,
    initiatorID: number,
    opponentsIDs: number[],
    callType: CallType,
    maxBandwidth: number = 0
  ): WebRTCSession {
    const webRTCSession = new WebRTCSession({
      ID: sessionID,
      initiatorID,
      opponentsIDs,
      callType,
      signalingProvider: this.signalingProvider,
      currentUserID: this.currentUserID,
      maxBandwidth,
    });

    webRTCSession.onUserNotAnswerListener = this.onUserNotAnswerListener;
    webRTCSession.onRemoteStreamListener = this.onRemoteStreamListener;
    webRTCSession.onSessionConnectionStateChangedListener = this.onSessionConnectionStateChangedListener;
    webRTCSession.onSessionCloseListener = this.onSessionCloseListener;
    webRTCSession.onCallStatsReportListener = this.onCallStatsReport;

    if (webRTCSession?.ID) {
      this.sessions[webRTCSession.ID] = webRTCSession;
    }

    return webRTCSession;
  }

  public clearSession(sessionId: string): void {
    delete this.sessions[sessionId];
  }

  /// Reject call by http request
  public callRejectRequest(params: {
    sessionID: string;
    recipientId: number;
    platform: 'web' | 'android' | 'ios';
    userInfo?: Calls.UserInfo;
  }): Promise<EmptyResponse> {
    const ajaxParams = {
      type: ProxyMethod.POST,
      url: Utils.getUrl(config.urls.calls, 'reject'),
      data: params,
    };

    return this.proxy.ajax(ajaxParams);
  }

  /// DELEGATE (signaling)

  public onCallHandler(userID: number, sessionID: string, extension: Calls.ExtensionParams): void {
    const userInfo = extension.userInfo || {};
    const maxBandwidth = Number(userInfo.maxBandwidth ?? 0);

    let session = this.sessions[sessionID];

    WebRTCHelpers.trace(`onCall. userID: ${userID}, sessionID: ${sessionID}, extension: ${JSON.stringify(extension)}`);

    if (!session) {
      session = this.createAndStoreSession(
        sessionID,
        extension.callerID || 0,
        extension.opponentsIDs || [],
        extension.callType || CallType.VIDEO,
        maxBandwidth
      );

      Utils.safeCallbackCall(this.onCallListener)(session, userInfo);
    }

    session.processOnCall(userID, extension);
  }

  public onAcceptHandler(userID: number, sessionID: string, extension: Calls.ExtensionParams): void {
    const session = this.sessions[sessionID];
    const userInfo = extension.userInfo || {};

    WebRTCHelpers.trace(
      `onAccept. UserID: ${userID}, sessionID: ${sessionID}, extension: ${JSON.stringify(extension)}`
    );

    if (session && (session.state === CallSessionState.ACTIVE || session.state === CallSessionState.NEW)) {
      Utils.safeCallbackCall(this.onAcceptCallListener)(session, userID, userInfo);
      session.processOnAccept(userID, extension);
    } else {
      WebRTCHelpers.traceWarning(`Ignore 'onAccept', there is no information about session ${sessionID}`);
    }
  }

  public onRejectHandler(userID: number, sessionID: string, extension: Calls.ExtensionParams): void {
    const session = this.sessions[sessionID];

    WebRTCHelpers.trace(
      `onReject. UserID: ${userID}, sessionID: ${sessionID}, extension: ${JSON.stringify(extension)}`
    );

    if (session) {
      const userInfo = extension.userInfo || {};

      Utils.safeCallbackCall(this.onRejectCallListener)(session, userID, userInfo);
      session.processOnReject(userID);
    } else {
      WebRTCHelpers.traceWarning(`Ignore 'onReject', there is no information about session ${sessionID}`);
    }
  }

  public onStopHandler(userID: number, sessionID: string, extension: Calls.ExtensionParams) {
    WebRTCHelpers.trace(`onStop. UserID: ${userID}, sessionID: ${sessionID}, extension: ${JSON.stringify(extension)}`);

    const session = this.sessions[sessionID];
    const userInfo = extension.userInfo || {};

    if (session && (session.state === CallSessionState.ACTIVE || session.state === CallSessionState.NEW)) {
      session.processOnStop(userID);
      Utils.safeCallbackCall(this.onStopCallListener)(session, userID, userInfo);
    } else {
      Utils.safeCallbackCall(this.onInvalidEventsListener)(session, userID, userInfo);
      WebRTCHelpers.traceWarning(`Ignore 'onStop', there is no information about session ${sessionID} by some reason`);
    }
  }

  public onIceCandidatesHandler(userID: number, sessionID: string, extension: Calls.ExtensionParams) {
    const session = this.sessions[sessionID];

    WebRTCHelpers.trace(
      `onIceCandidates. UserID: ${userID}. SessionID: ${sessionID}. ICE candidates count: ${
        extension.iceCandidates?.length ?? 0
      }`
    );

    if (session) {
      if (session.state === CallSessionState.ACTIVE) {
        session.processOnIceCandidates(userID, extension);
      } else {
        WebRTCHelpers.traceWarning(`Ignore 'OnIceCandidates', the session ( ${sessionID} ) has invalid state`);
      }
    } else {
      WebRTCHelpers.traceWarning(`Ignore 'OnIceCandidates', there is no information about session ${sessionID}`);
    }
  }

  public onIceRestartHandler(userID: number, sessionID: string, extension: Calls.ExtensionParams) {
    const session = this.sessions[sessionID];

    WebRTCHelpers.trace(
      `onIceRestart. UserID: ${userID}. SessionID: ${sessionID}. Extension: ${JSON.stringify(extension)}`
    );

    if (session) {
      if (session.state === CallSessionState.ACTIVE) {
        session.processOnIceRestart(userID, extension);
      } else {
        WebRTCHelpers.traceWarning(`Ignore 'OnIceRestart', the session (${sessionID}) has invalid state`);
      }
    } else {
      WebRTCHelpers.traceWarning(`Ignore 'OnIceRestart', there is no information about session ${sessionID}`);
    }
  }

  public onIceRestartAcceptHandler(userID: number, sessionID: string, extension: Calls.ExtensionParams) {
    const session = this.sessions[sessionID];

    WebRTCHelpers.trace(
      `onIceRestartAccept. UserID: ${userID}. SessionID: ${sessionID}. Extension: ${JSON.stringify(extension)}`
    );

    if (session) {
      if (session.state === CallSessionState.ACTIVE) {
        session.processOnIceRestartAccept(userID, extension);
      } else {
        WebRTCHelpers.traceWarning(`Ignore 'onIceRestartAccept', the session (${sessionID}) has invalid state`);
      }
    } else {
      WebRTCHelpers.traceWarning(`Ignore 'onIceRestartAccept', there is no information about session ${sessionID}`);
    }
  }

  private getListenerByName(name: CallEvent): Calls.ListenerName | null {
    switch (name) {
      case CallEvent.CALL:
        return 'onCallListener';
      case CallEvent.ACCEPT:
        return 'onAcceptCallListener';
      case CallEvent.REJECT:
        return 'onRejectCallListener';
      case CallEvent.STOP:
        return 'onStopCallListener';
      case CallEvent.INVALID:
        return 'onInvalidEventsListener';
      case CallEvent.NOT_ANSWER:
        return 'onUserNotAnswerListener';
      case CallEvent.REMOTE_STREAM:
        return 'onRemoteStreamListener';
      case CallEvent.CONNECTION_STATE:
        return 'onSessionConnectionStateChangedListener';
      case CallEvent.CLOSE:
        return 'onSessionCloseListener';
      case CallEvent.STATS_REPORT:
        return 'onCallStatsReport';
      case CallEvent.DEVICES:
        return 'onDevicesChangeListener';
      default:
        return null;
    }
  }

  public addListener(name: CallEvent, listener: Calls.Listeners): () => void {
    const listenerName = this.getListenerByName(name);
    if (listenerName) {
      (this[listenerName] as Calls.Listeners) = listener;
    }
    return this.removeListener.bind(this, name);
  }

  public removeListener(name: CallEvent): void {
    const listenerName = this.getListenerByName(name);
    if (listenerName) {
      (this[listenerName] as Calls.Listeners | undefined) = name === CallEvent.DEVICES ? () => {} : undefined;
    }
  }

  public removeAllListeners(): void {
    Object.keys(this).forEach((key) => {
      if (key.startsWith('on') && key.endsWith('Listener') && typeof this[key as Calls.ListenerName] === 'function') {
        (this[key as Calls.ListenerName] as Calls.Listeners | undefined) =
          key === 'onDevicesChangeListener' ? () => {} : undefined;
      }
    });
  }
}
